/**
 * Basic 组件 Widget 类（6.2 BaseWidget 体系）
 * Text / Button / Input / Textarea / Select / Switch / Checkbox / Radio / DatePicker / Slider
 *
 * 表单类组件按 Input 的既有模式声明 6.3 行为钩子：
 * - default: required / value 等默认值
 * - meta: isFocused / isOpen / checked / value（瞬态，不持久化但可绑定）
 * - derived: isValid = {{ this.required ? this.value && this.value.length > 0 : true }}
 * 纯展示型（Text）保持从简。
 */
import type { PropertyPaneConfig, PropSchema } from "@shared/types";

import { getMaterial } from "../materials";
import { BaseWidget } from "./base-widget";
import {
  buildEventBinderSection,
  buildMaterialPropertyPaneConfig,
} from "./property-pane";

/** 文本类必填校验表达式（required 时 value 非空） */
const REQUIRED_VALUE_VALID_EXPR = "{{ this.required ? this.value && this.value.length > 0 : true }}";

/** 布尔类必填校验表达式（required 时必须勾选/开启） */
const REQUIRED_CHECKED_VALID_EXPR = "{{ this.required ? this.checked : true }}";

/**
 * “校验” section：派生 isValid + 瞬态 isFocused（InputWidget 既有模式的复用，
 * 供 Textarea / Select / Checkbox / Radio / DatePicker 等表单组件共享）。
 */
function buildValidationPaneSection(isValidExpression: string): PropertyPaneConfig {
  return {
    sectionName: "校验",
    children: [
      {
        propertyName: "isValid",
        label: "校验通过",
        controlType: "DATA_BINDING",
        propertyType: "derived",
        isBindProperty: true,
        helpText: `派生属性：${isValidExpression}`,
      },
      {
        propertyName: "isFocused",
        label: "聚焦状态",
        controlType: "SWITCH",
        propertyType: "meta",
        isBindProperty: true,
        defaultValue: false,
        helpText: "瞬态属性（运行时状态，不持久化）",
      },
    ],
  };
}

function paneConfigFor(type: string, extraSections: PropertyPaneConfig[] = []): PropertyPaneConfig[] {
  return buildMaterialPropertyPaneConfig(getMaterial(type)?.propSchema ?? [], extraSections);
}

export class TextWidget extends BaseWidget {
  getWidgetType(): string {
    return "Text";
  }
  getPropertyPaneConfig(): PropertyPaneConfig[] {
    return paneConfigFor("Text");
  }
}

export class ButtonWidget extends BaseWidget {
  getWidgetType(): string {
    return "Button";
  }
  getPropertyPaneConfig(): PropertyPaneConfig[] {
    return paneConfigFor("Button", [
      buildEventBinderSection([{ event: "onClick", label: "点击" }]),
    ]);
  }
  getMetaProperties(): Record<string, unknown> {
    return { isPressed: false, isHovered: false };
  }
}

export class InputWidget extends BaseWidget {
  getWidgetType(): string {
    return "Input";
  }
  getPropertyPaneConfig(): PropertyPaneConfig[] {
    const propSchema = getMaterial("Input")?.propSchema ?? ([] as PropSchema[]);
    return buildMaterialPropertyPaneConfig(propSchema, [
      buildValidationPaneSection(REQUIRED_VALUE_VALID_EXPR),
    ]);
  }
  getDefaultProperties(): Record<string, unknown> {
    return { required: false, value: "" };
  }
  getMetaProperties(): Record<string, unknown> {
    return { isFocused: false, value: "" };
  }
  getDerivedProperties(): Record<string, string> {
    return {
      isValid: REQUIRED_VALUE_VALID_EXPR,
    };
  }
}

export class TextareaWidget extends BaseWidget {
  getWidgetType(): string {
    return "Textarea";
  }
  getPropertyPaneConfig(): PropertyPaneConfig[] {
    return paneConfigFor("Textarea", [buildValidationPaneSection(REQUIRED_VALUE_VALID_EXPR)]);
  }
  getDefaultProperties(): Record<string, unknown> {
    return { required: false, value: "" };
  }
  getMetaProperties(): Record<string, unknown> {
    return { isFocused: false, value: "" };
  }
  getDerivedProperties(): Record<string, string> {
    return { isValid: REQUIRED_VALUE_VALID_EXPR };
  }
}

export class SelectWidget extends BaseWidget {
  getWidgetType(): string {
    return "Select";
  }
  getPropertyPaneConfig(): PropertyPaneConfig[] {
    return paneConfigFor("Select", [buildValidationPaneSection(REQUIRED_VALUE_VALID_EXPR)]);
  }
  getDefaultProperties(): Record<string, unknown> {
    return { required: false, value: "" };
  }
  getMetaProperties(): Record<string, unknown> {
    return { isOpen: false, value: "" };
  }
  getDerivedProperties(): Record<string, string> {
    return { isValid: REQUIRED_VALUE_VALID_EXPR };
  }
}

export class SwitchWidget extends BaseWidget {
  getWidgetType(): string {
    return "Switch";
  }
  getPropertyPaneConfig(): PropertyPaneConfig[] {
    return paneConfigFor("Switch");
  }
  getDefaultProperties(): Record<string, unknown> {
    return { checked: false };
  }
  getMetaProperties(): Record<string, unknown> {
    return { checked: false };
  }
}

export class CheckboxWidget extends BaseWidget {
  getWidgetType(): string {
    return "Checkbox";
  }
  getPropertyPaneConfig(): PropertyPaneConfig[] {
    return paneConfigFor("Checkbox", [buildValidationPaneSection(REQUIRED_CHECKED_VALID_EXPR)]);
  }
  getDefaultProperties(): Record<string, unknown> {
    return { required: false, checked: false };
  }
  getMetaProperties(): Record<string, unknown> {
    return { isFocused: false, checked: false };
  }
  getDerivedProperties(): Record<string, string> {
    return { isValid: REQUIRED_CHECKED_VALID_EXPR };
  }
}

export class RadioWidget extends BaseWidget {
  getWidgetType(): string {
    return "Radio";
  }
  getPropertyPaneConfig(): PropertyPaneConfig[] {
    return paneConfigFor("Radio", [buildValidationPaneSection(REQUIRED_VALUE_VALID_EXPR)]);
  }
  getDefaultProperties(): Record<string, unknown> {
    return { required: false, value: "" };
  }
  getMetaProperties(): Record<string, unknown> {
    return { isFocused: false, value: "" };
  }
  getDerivedProperties(): Record<string, string> {
    return { isValid: REQUIRED_VALUE_VALID_EXPR };
  }
}

export class DatePickerWidget extends BaseWidget {
  getWidgetType(): string {
    return "DatePicker";
  }
  getPropertyPaneConfig(): PropertyPaneConfig[] {
    return paneConfigFor("DatePicker", [buildValidationPaneSection(REQUIRED_VALUE_VALID_EXPR)]);
  }
  getDefaultProperties(): Record<string, unknown> {
    return { required: false, value: "" };
  }
  getMetaProperties(): Record<string, unknown> {
    return { isOpen: false, value: "" };
  }
  getDerivedProperties(): Record<string, string> {
    return { isValid: REQUIRED_VALUE_VALID_EXPR };
  }
}

export class SliderWidget extends BaseWidget {
  getWidgetType(): string {
    return "Slider";
  }
  getPropertyPaneConfig(): PropertyPaneConfig[] {
    return paneConfigFor("Slider");
  }
  getDefaultProperties(): Record<string, unknown> {
    return { value: 50 };
  }
  getMetaProperties(): Record<string, unknown> {
    return { value: 50, isDragging: false };
  }
}
